import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  ConfirmationMessageModal,
  EmailsTable,
  FullScreenLoader,
  MenuItem,
  PrintLayout,
  ScrollToTop,
  TableActionsMenu,
} from '@Components/index';
import { CommonConstants } from '@Constants/index';
import EmailsModal from '@Containers/Emails/EmailsModal';
import { EmailsQuery } from '@Models/emails.models';
import {
  useDeleteGlobalEmailMutation,
  useDeleteGlobalEmailsMutation,
  useGetGlobalEmailsQuery,
} from '@Services/globalEmailTemplates.service';
import {
  BaseAlertProps,
  ConfirmationActionTypes,
  DialerCrudTypes,
  DialerTypes,
} from '@Typings/global.types';
import {
  getConfirmationMessageNode,
  getModalBtnComponent,
  getResponseData,
  GetRows,
  getRTKQuery,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';
import usePrint from '@Utils/hooks/usePrint.hook';

const AdminEmails: FunctionComponent = (): JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();
  const { triggerAlert } = useAlert();
  const { componentRef, handlePrint, setPrintState } = usePrint(true);

  const [checked, setChecked] = useState<readonly string[]>([]);
  const [modalCrudType, setModalCrudType] = useState<DialerCrudTypes | null>(
    null
  );
  const [emailId, setEmailId] = useState<string>('');
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [itemsCount, setItemsCount] = useState<number>(0);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationActionType, setConfirmationActionType] =
    useState<ConfirmationActionTypes | null>(null);
  const [isBulkAction, setIsBulkAction] = useState(false);

  const emailsActiveQuery: EmailsQuery = useMemo(
    () => getRTKQuery(location.search),
    [location.search]
  );

  const {
    data,
    isLoading,
    isFetching,
    error: getEmailsError,
  } = useGetGlobalEmailsQuery({
    data: emailsActiveQuery,
  });

  const [deleteGlobalEmail, deleteGlobalEmailMethods] =
    useDeleteGlobalEmailMutation();
  const [deleteGlobalEmails, deleteGlobalEmailsMethods] =
    useDeleteGlobalEmailsMutation();

  const loading =
    isLoading ||
    isFetching ||
    deleteGlobalEmailMethods.isLoading ||
    deleteGlobalEmailsMethods.isLoading;

  useEffect(() => {
    if (getEmailsError) {
      triggerAlert(getEmailsError, setAlertState);
    }
  }, [getEmailsError, triggerAlert]);

  useEffect(() => {
    setEmailId('');
    setIsBulkAction(false);
    setItemsCount(0);

    if (
      deleteGlobalEmailMethods.isSuccess ||
      deleteGlobalEmailsMethods.isSuccess
    ) {
      setChecked([]);
    }
  }, [deleteGlobalEmailMethods.isSuccess, deleteGlobalEmailsMethods.isSuccess]);

  useEffect(() => {
    if (
      deleteGlobalEmailMethods.isSuccess ||
      deleteGlobalEmailsMethods.isSuccess
    ) {
      triggerAlert(CommonConstants.GLOBAL_MESSAGES.DELETE_EMAIL, setAlertState);
    }

    if (deleteGlobalEmailMethods.error) {
      triggerAlert(deleteGlobalEmailMethods.error, setAlertState);
    }
    if (deleteGlobalEmailsMethods.error) {
      triggerAlert(deleteGlobalEmailsMethods.error, setAlertState);
    }
  }, [
    triggerAlert,
    deleteGlobalEmailMethods.isSuccess,
    deleteGlobalEmailMethods.error,
    deleteGlobalEmailsMethods.isSuccess,
    deleteGlobalEmailsMethods.error,
  ]);

  const handleEmailAdd = () => {
    setModalCrudType(DialerCrudTypes.Add);
    setEmailId('');
  };

  const modalBtnComponent = (id: string, name: string): JSX.Element => {
    const handleClick = () => {
      setModalCrudType(DialerCrudTypes.View);
      setEmailId(id);
    };

    return getModalBtnComponent({
      id,
      name,
      onClick: handleClick,
      isAdmin: true,
    });
  };

  const actionsComponent = (
    id: string,
    disabled?: boolean,
    defaultType?: boolean
  ): JSX.Element => {
    const handleTableActionsClick = (
      confirmationType: ConfirmationActionTypes
    ) => {
      setIsConfirmationModalOpen(true);
      setConfirmationActionType(confirmationType);
    };

    const actionsMenu: MenuItem[] = [
      {
        label: t('common.messages.Edit'),
        icon: 'edit',
        value: id,
        onClick: () => {
          setModalCrudType(DialerCrudTypes.Edit);
          setEmailId(id);
        },
      },
      {
        label: t('common.messages.Delete'),
        icon: 'delete',
        value: id,
        onClick: () => {
          setItemsCount(0);
          setIsBulkAction(false);
          setEmailId(id);
          handleTableActionsClick(ConfirmationActionTypes.Delete);
        },
        disabled: defaultType,
      },
      {
        label: t('common.messages.Print'),
        icon: 'print',
        value: id,
        onClick: () => {
          setPrintState(true);
          if (emailId === id) {
            handlePrint();
          } else {
            setEmailId(id);
          }
        },
      },
    ];

    return <TableActionsMenu menuItems={actionsMenu} disabled={disabled} />;
  };

  const rows = GetRows(data?.list, actionsComponent, modalBtnComponent, true);
  const totalRowsCount = data?.meta.count
    ? parseInt(data.meta.count, 10)
    : CommonConstants.INITIAL_ROWS_PER_PAGE;

  const handleBulk = (count: number) => setItemsCount(count);

  const handleBulkActionBtnClick = (
    confirmationType: ConfirmationActionTypes
  ) => {
    setConfirmationActionType(confirmationType);
    setIsBulkAction(true);
    setIsConfirmationModalOpen(true);
  };

  useEffect(() => {
    if (emailId) {
      handlePrint();
    }
  }, [emailId, handlePrint]);

  const handleConfirmationModalClick = () => {
    setIsConfirmationModalOpen(false);
    if (confirmationActionType === ConfirmationActionTypes.Delete) {
      return isBulkAction
        ? deleteGlobalEmails({ ids: checked })
        : deleteGlobalEmail(emailId);
    }
  };

  const onConfirmationModalClose = () => {
    setEmailId('');
  };

  return !isLoading ? (
    <>
      <ScrollToTop
        dependencies={[
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) || '',
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) || '',
        ]}
      />
      <PrintLayout
        ref={componentRef}
        printData={{
          name: getResponseData(data?.list, emailId)?.name,
          listType: getResponseData(data?.list, emailId)?.type,
          isDefault: getResponseData(data?.list, emailId)?.default,
          subject: getResponseData(data?.list, emailId)?.subject,
          body: getResponseData(data?.list, emailId)?.message,
        }}
      />
      <EmailsModal
        isAdmin
        emailId={emailId}
        setEmailId={setEmailId}
        crudType={modalCrudType}
        setCrudType={setModalCrudType}
        deleteBtnOnClick={() => deleteGlobalEmail(emailId)}
      />
      <ConfirmationMessageModal
        isModalOpen={isConfirmationModalOpen}
        setIsModalOpen={setIsConfirmationModalOpen}
        message={getConfirmationMessageNode(confirmationActionType)}
        // headerText={t('common.confirmationModal.Attention')}
        // cancelButtonText={t('common.confirmationModal.Close')}
        onClick={handleConfirmationModalClick}
        onClose={onConfirmationModalClose}
      />
      <EmailsTable
        isAdmin
        heading={t('Email.Global Email Templates')}
        tabChild={!!query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID)}
        selectedBtnNumber={itemsCount || checked.length}
        alertState={alertState}
        EmailTemplateType={DialerTypes.Active}
        deleteBtnProps={{
          onClick: () => {
            handleBulkActionBtnClick(ConfirmationActionTypes.Delete);
          },
          disabled: loading,
        }}
        emailsAddBtnProps={{
          onClick: handleEmailAdd,
        }}
        tableProps={{
          loading,
          rows,
          checked,
          setChecked,
          paginationProps: {
            count: totalRowsCount,
          },
        }}
        handleBulk={handleBulk}
      />
    </>
  ) : (
    <FullScreenLoader />
  );
};

export default AdminEmails;
